import React, { useMemo, useState, useEffect } from 'react';
import { Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
} from 'chart.js';
import { Transaction, TransactionType, PaymentType } from '../types/Transaction';
import { Category } from '../types/Category';
import { getCategorySpend, CategorySeries } from '../services/api';
import AskExplain from './AskExplain';
import { useTokenCss } from '../utils/tokenCss';
import './CategoryChart.css';
import { formatPct, brlOrMasked } from '../utils/format';
import { useGerenciadorDeCategorias } from './GerenciadorDeCategorias';
import { valorExibido } from '../utils/monthView';

ChartJS.register(ArcElement, Tooltip, Legend);

function toPastel(hex: string): string {
  if (!hex || !hex.startsWith('#')) return hex;
  let r: number, g: number, b: number;
  if (hex.length === 4) {
    r = parseInt(hex[1] + hex[1], 16);
    g = parseInt(hex[2] + hex[2], 16);
    b = parseInt(hex[3] + hex[3], 16);
  } else if (hex.length === 7) {
    r = parseInt(hex.slice(1, 3), 16);
    g = parseInt(hex.slice(3, 5), 16);
    b = parseInt(hex.slice(5, 7), 16);
  } else {
    return hex;
  }
  const mix = 0.52;
  const pr = Math.round(r * mix + 255 * (1 - mix));
  const pg = Math.round(g * mix + 255 * (1 - mix));
  const pb = Math.round(b * mix + 255 * (1 - mix));
  return `#${pr.toString(16).padStart(2, '0')}${pg.toString(16).padStart(2, '0')}${pb.toString(16).padStart(2, '0')}`;
}

interface CategoryChartProps {
  /** O olhinho de "Ocultar valores" do cabeçalho. Ver `utils/format.ts`. */
  hideValues?: boolean;

  transactions: Transaction[];
  categories: Category[];
  activeMonthLabel: string;
  onCategoryClick?: (categoryId: string) => void;
  /**
   * Criou, editou ou excluiu uma categoria aqui dentro — recarregue a lista.
   *
   * O gerenciador é o mesmo do formulário de transação. Este card é o lugar
   * mais óbvio de procurar por "editar categoria", e era o único que não
   * tinha.
   */
  onCategoriasMudaram?: () => void;

  /**
   * Navegar o mês DAQUI move o mês do app inteiro, e não só desta seção.
   *
   * Um mês local aqui deixaria a rosca falando de março enquanto os cards do
   * topo falam de agosto — duas verdades sobre meses diferentes na mesma tela,
   * que é a divergência silenciosa que este app já teve três vezes com o
   * saldo. O controle é outro; o mês é o mesmo.
   */
  onNavigateMonth?: (direcao: 1 | -1) => void;
  /** `undefined` quando já estamos no mês corrente — aí o chip não aparece. */
  onVoltarParaHoje?: () => void;
}

/**
 * O dado que alimenta o "Exemplo" do estado vazio.
 *
 * Proporções escolhidas para o donut ficar legível: nem uma fatia dominante
 * que engole as outras, nem quatro fatias iguais, que não parecem gasto real.
 */
const EXEMPLO_CATS: Category[] = [
  { id: 'ex-1', name: 'Alimentação', color: '#f59e0b', createdAt: '' },
  { id: 'ex-2', name: 'Moradia',     color: '#3b82f6', createdAt: '' },
  { id: 'ex-3', name: 'Lazer',       color: '#ec4899', createdAt: '' },
  { id: 'ex-4', name: 'Transporte',  color: '#10b981', createdAt: '' },
];

const EXEMPLO_TX: Transaction[] = [926, 731, 439, 342].map((valor, i) => ({
  id: `ex-t${i}`,
  type: TransactionType.EXPENSE,
  paymentType: PaymentType.VARIABLE,
  description: EXEMPLO_CATS[i].name,
  value: valor,
  hasInstallments: false,
  isMonthly: false,
  paymentMethod: null,
  date: '',
  categoryId: EXEMPLO_CATS[i].id,
  createdAt: '',
}));

const CategoryChart: React.FC<CategoryChartProps> = ({
  transactions,
  categories,
  onCategoriasMudaram,
  activeMonthLabel,
  onCategoryClick,
  hideValues,
  onNavigateMonth,
  onVoltarParaHoje,
}) => {
  const gerenciador = useGerenciadorDeCategorias({
    categories,
    aoMudar: onCategoriasMudaram,
    // O marcador do Finn ("é essencial?") traz a pessoa até aqui.
    abreAoPedir: true,
  });

  const [showLegend, setShowLegend] = useState(() => {
    const saved = localStorage.getItem('cat-chart-show-legend');
    return saved === null ? true : saved === 'true';
  });

  const toggleLegend = () => {
    setShowLegend(v => {
      localStorage.setItem('cat-chart-show-legend', String(!v));
      return !v;
    });
  };

  /* A borda entre as fatias é a superfície do card, não branco fixo: em canvas
     o `var()` não resolve, então o valor tem que chegar já lido. */
  const corDoCard = useTokenCss('--s-card');

  /* Delega para `brlOrMasked`: é o que faz o olhinho de "Ocultar valores"
     alcançar esta seção. Antes era um `Intl.NumberFormat` próprio, e como não
     havia um lugar por onde todo dinheiro passasse, o olhinho só chegava nos
     quatro cartões do topo. */
  const fmt = (v: number) => brlOrMasked(v, !!hideValues);

  const grouped = useMemo(() => {
    const map = new Map<string, { id: string; label: string; value: number; color: string }>();

    transactions
      .filter(t => t.type === TransactionType.EXPENSE)
      .forEach(t => {
        const catId = t.categoryId ?? '__none__';
        const cat = categories.find(c => c.id === catId);
        const label = cat?.name ?? 'Sem categoria';
        const color = cat?.color ?? '#d1d5db';

        // O valor do mês de uma mensal variável, não o cadastrado (15/09).
        if (map.has(catId)) {
          map.get(catId)!.value += valorExibido(t);
        } else {
          map.set(catId, { id: catId, label, value: valorExibido(t), color });
        }
      });

    return Array.from(map.values()).sort((a, b) => b.value - a.value);
  }, [transactions, categories]);

  const total = grouped.reduce((s, g) => s + g.value, 0);

  if (grouped.length === 0) {
    /**
     * O exemplo É este mesmo componente, com dado de mentira.
     *
     * Antes era um donut desenhado à mão, com arcos e legenda próprios. Ele
     * nasceu parecido e foi ficando diferente: a faixa de estatísticas do
     * gráfico real nunca chegou nele, e a legenda seguiu outro caminho. Um
     * "Exemplo" que não mostra o que vai acontecer é pior que nenhum, porque
     * promete errado.
     *
     * Recursão termina porque `EXEMPLO_TX` não é vazio.
     */
    return (
      <div className="cat-chart-container">
        <div className="cat-chart-header">
          <div className="cat-chart-title-group">
            <h2>Gastos por Categoria</h2>
          </div>
          {/*
            * O gerenciador aparece TAMBÉM no estado vazio, e ali é onde ele
            * mais serve: quem ainda não tem gasto categorizado é justamente
            * quem pode querer criar a primeira categoria.
            */}
          <div className="cat-chart-header-right">
            {gerenciador.botoes}
            <span className="cat-chart-month">{activeMonthLabel}</span>
          </div>
        </div>
        {/*
          * Os painéis FORA do cabeçalho, e em largura cheia.
          *
          * Dentro dele — que é `flex: nowrap` — eles ficavam lado a lado com o
          * botão "Lista": o cabeçalho esticava de 34px para 264, e a lista de
          * categorias virava uma coluna espremida de 191px. Foi o que ela viu.
          */}
        {gerenciador.paineis}
        <div className="empty-preview-wrap">
          <span className="empty-preview-badge">Exemplo</span>
          <div className="empty-preview-ghost cat-preview-real">
            <CategoryChart
              transactions={EXEMPLO_TX}
              categories={EXEMPLO_CATS}
              activeMonthLabel={activeMonthLabel}
            />
          </div>
        </div>
        <div className="empty-cta">
          <p className="empty-cta-title">Nenhum gasto categorizado</p>
          <p className="empty-cta-sub">Registre transações com categorias para visualizar o gráfico de distribuição de gastos.</p>
        </div>
      </div>
    );
  }


  const chartData = {
    labels: grouped.map(g => g.label),
    datasets: [
      {
        data: grouped.map(g => g.value),
        backgroundColor: grouped.map(g => toPastel(g.color)),
        borderColor: corDoCard,
        borderWidth: 4,
        hoverOffset: 10,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '72%',
    animation: {
      animateRotate: true,
      animateScale: true,
      duration: 900,
      easing: 'easeInOutQuart' as const,
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx: any) => {
            const val = ctx.raw as number;
            const pct = total > 0 ? formatPct((val / total) * 100) : formatPct(0);
            return `  ${fmt(val)}  (${pct})`;
          },
        },
        backgroundColor: 'rgba(17,24,39,0.92)',
        padding: 12,
        cornerRadius: 10,
        titleFont: { size: 13, weight: 'bold' as const },
        bodyFont: { size: 12 },
      },
    },
  };

  const top = grouped[0];
  const avg = total / grouped.length;
  const multiCol = grouped.length >= 6;

  return (
    <div className="cat-chart-container">
      {/* Header */}
      <div className="cat-chart-header">
        <div className="cat-chart-title-group">
          <h2>Gastos por Categoria</h2>
          <span className="cat-chart-count">{grouped.length} {grouped.length === 1 ? 'categoria' : 'categorias'}</span>
        </div>
        <div className="cat-chart-header-right">
          {gerenciador.botoes}
          <button
            className="cat-legend-toggle"
            onClick={toggleLegend}
            title={showLegend ? 'Ocultar lista' : 'Mostrar lista'}
          >
            {showLegend ? (
              <svg width="15" height="15" viewBox="0 0 15 15" fill="none">
                <rect x="1" y="3" width="13" height="1.8" rx="0.9" fill="currentColor"/>
                <rect x="1" y="6.6" width="13" height="1.8" rx="0.9" fill="currentColor"/>
                <rect x="1" y="10.2" width="13" height="1.8" rx="0.9" fill="currentColor"/>
              </svg>
            ) : (
              <svg width="15" height="15" viewBox="0 0 15 15" fill="none">
                <rect x="1" y="3" width="13" height="1.8" rx="0.9" fill="currentColor" opacity="0.35"/>
                <rect x="1" y="6.6" width="13" height="1.8" rx="0.9" fill="currentColor" opacity="0.35"/>
                <rect x="1" y="10.2" width="13" height="1.8" rx="0.9" fill="currentColor" opacity="0.35"/>
                <line x1="2" y1="13" x2="13" y2="2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
              </svg>
            )}
            <span>{showLegend ? 'Lista' : 'Lista'}</span>
          </button>
          {!onNavigateMonth && (
            <span className="cat-chart-month">{activeMonthLabel}</span>
          )}
        </div>
      </div>

      {/* Fora do cabeçalho, e em largura cheia — ver a nota no estado vazio. */}
      {gerenciador.paineis}

      {/*
        O seletor de mês tem linha própria, centralizado.

        Ele morava dentro do cabeçalho, disputando espaço com o título e o
        botão de lista. No celular a linha quebrava e virava uma pilha: as duas
        setas empilhadas de um lado, o mês e o "Hoje" do outro.

        Aqui ele é uma faixa própria, e o "Hoje" cai centralizado embaixo do
        mês — nos dois tamanhos de tela, não só no celular.
      */}
      {onNavigateMonth && (
        <div className="cat-mes">
          <div className="cat-mes-nav">
            <button
              className="cat-mes-seta"
              onClick={() => onNavigateMonth(-1)}
              title="Mês anterior"
              aria-label="Mês anterior"
            >‹</button>
            <span className="cat-chart-month">{activeMonthLabel}</span>
            <button
              className="cat-mes-seta"
              onClick={() => onNavigateMonth(1)}
              title="Próximo mês"
              aria-label="Próximo mês"
            >›</button>
          </div>
          {/* Só quando ela saiu do mês corrente: sempre visível, seria mais um
              botão parado dizendo onde ela já está. */}
          {onVoltarParaHoje && (
            <button className="cat-mes-hoje" onClick={onVoltarParaHoje}>Hoje</button>
          )}
        </div>
      )}

      {/* Stats strip */}
      <div className="cat-stats-strip">
        <div className="cat-stat-item">
          <span className="cat-stat-label">Total de gastos</span>
          <span className="cat-stat-value expense">{fmt(total)}</span>
        </div>
        <div className="cat-stat-divider" />
        <div className="cat-stat-item">
          <span className="cat-stat-label">Maior categoria</span>
          <span className="cat-stat-value" style={{ color: top.color }}>
            {top.label}
          </span>
        </div>
        <div className="cat-stat-divider" />
        <div className="cat-stat-item">
          <span className="cat-stat-label">Média por categoria</span>
          <span className="cat-stat-value neutral">{fmt(avg)}</span>
        </div>
      </div>

      <div className="cat-chart-body">
        {/* Donut */}
        <div className="cat-chart-left">
          {/* SVG donut com labels inline — mobile sem lista */}
          <svg viewBox="0 0 300 220" className={`cat-svg-donut${!showLegend ? ' cat-svg-donut-visible' : ''}`}>
            {(() => {
              const cx = 150, cy = 110, outerR = 72, innerR = 52, gap = 0.025;
              let cum = -Math.PI / 2;
              return grouped.map((g, i) => {
                const frac = g.value / total;
                const angle = frac * 2 * Math.PI;
                const start = cum + gap / 2;
                const end = cum + angle - gap / 2;
                const mid = cum + angle / 2;
                cum += angle;

                const cos = Math.cos(mid), sin = Math.sin(mid);
                const p = (r: number) => [cx + r * cos, cy + r * sin] as [number, number];
                const large = end - start > Math.PI ? 1 : 0;
                const [sx, sy] = [cx + outerR * Math.cos(start), cy + outerR * Math.sin(start)];
                const [ex, ey] = [cx + outerR * Math.cos(end), cy + outerR * Math.sin(end)];
                const [iex, iey] = [cx + innerR * Math.cos(end), cy + innerR * Math.sin(end)];
                const [isx, isy] = [cx + innerR * Math.cos(start), cy + innerR * Math.sin(start)];
                const d = `M ${sx} ${sy} A ${outerR} ${outerR} 0 ${large} 1 ${ex} ${ey} L ${iex} ${iey} A ${innerR} ${innerR} 0 ${large} 0 ${isx} ${isy} Z`;

                const showLabel = frac >= 0.04;
                const [lx, ly] = p(outerR + 9);
                const [tx, ty] = p(outerR + 16);
                const isRight = cos >= 0;
                const label = g.label.length > 12 ? g.label.slice(0, 11) + '…' : g.label;

                return (
                  <g key={i} style={{ cursor: onCategoryClick ? 'pointer' : 'default' }} onClick={() => onCategoryClick?.(g.id)}>
                    <path d={d} fill={toPastel(g.color)} style={{ stroke: 'var(--s-card)' }} strokeWidth="2.5" />
                    {showLabel && (
                      <>
                        <line x1={lx} y1={ly} x2={tx} y2={ty} stroke={g.color} strokeWidth="1" opacity="0.6" />
                        <text
                          x={isRight ? tx + 2 : tx - 2}
                          y={ty}
                          textAnchor={isRight ? 'start' : 'end'}
                          dominantBaseline="middle"
                          fontSize="11"
                          fill="#374151"
                          fontFamily="Inter,-apple-system,sans-serif"
                          fontWeight="600"
                        >{label}</text>
                      </>
                    )}
                  </g>
                );
              });
            })()}
            {/* Centro */}
            <text x="150" y="104" textAnchor="middle" fontSize="10" fill="#9ca3af" fontFamily="Inter,-apple-system,sans-serif" fontWeight="500">Total</text>
            <text x="150" y="120" textAnchor="middle" fontSize="12.5" fill="#dc2626" fontFamily="Inter,-apple-system,sans-serif" fontWeight="800">{fmt(total)}</text>
          </svg>

          {/* Chart.js donut — oculto no mobile quando lista está escondida */}
          <div className={`cat-chart-donut-wrap${!showLegend ? ' cat-chartjs-hidden' : ''}`}>
            <Doughnut data={chartData} options={chartOptions} />
            <div className="cat-chart-center">
              <span className="cat-chart-center-label">Total</span>
              <span className="cat-chart-center-value">{fmt(total)}</span>
            </div>
          </div>
        </div>

        {/* Legend — 2 colunas quando há 4+ categorias */}
        <div className={`cat-chart-legend${multiCol ? ' cat-chart-legend-grid' : ''}${!showLegend ? ' cat-legend-hidden' : ''}`}>
          {grouped.map((g, i) => {
            const pct = total > 0 ? (g.value / total) * 100 : 0;
            const pastel = toPastel(g.color);
            return (
              <div
                className={`cat-legend-item${onCategoryClick ? ' cat-legend-item-clickable' : ''}`}
                key={i}
                onClick={() => onCategoryClick?.(g.id)}
                title={onCategoryClick ? `Ver transações de ${g.label}` : undefined}
              >
                <span className="cat-legend-rank" style={{ background: pastel + '66', color: g.color }}>{i + 1}</span>
                <div className="cat-legend-info">
                  <div className="cat-legend-top-row">
                    <span className="cat-legend-name">{g.label}</span>
                    <span className="cat-legend-pct-pill" style={{ background: pastel + '55', color: g.color }}>
                      {formatPct(pct)}
                    </span>
                  </div>
                  <div className="cat-legend-bar-row">
                    <div className="cat-legend-bar-wrap">
                      <div className="cat-legend-bar" style={{ width: `${pct}%`, background: pastel }} />
                    </div>
                    <span className="cat-legend-amount">{fmt(g.value)}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <CategoryTrend hideValues={hideValues} />
      <AskExplain scope="categories" hideValues={hideValues} />
    </div>
  );
};

/**
 * Item 19 — tendência por categoria.
 *
 * A pizza acima responde "onde foi meu dinheiro NESTE mês". Isto responde
 * "pra onde meu dinheiro está indo", que é outra pergunta: precisa de série
 * temporal, não de foto. Fica recolhido por padrão para não competir com o
 * gráfico principal.
 */
const CategoryTrend: React.FC<{ hideValues?: boolean }> = ({ hideValues }) => {
  const [aberto, setAberto] = useState(() => localStorage.getItem('cat-trend-open') === 'true');
  const [series, setSeries] = useState<CategorySeries[] | null>(null);
  const [erro, setErro] = useState(false);

  useEffect(() => {
    if (!aberto || series || erro) return;
    getCategorySpend()
      .then(r => setSeries(r.series))
      .catch(() => setErro(true));
  }, [aberto, series, erro]);

  const toggle = () => {
    setAberto(v => { localStorage.setItem('cat-trend-open', String(!v)); return !v; });
  };

  /* Sem centavos: é média mensal, e o centavo de uma média é ruído. O olhinho
     alcança daqui igual ao resto — ver `utils/format.ts`. */
  const fmt = (v: number) => brlOrMasked(v, !!hideValues, { maximumFractionDigits: 0 });

  return (
    <div className="cat-trend">
      <button className="cat-trend-toggle" onClick={toggle} aria-expanded={aberto}>
        <span className={`cat-trend-caret${aberto ? ' open' : ''}`}>▸</span>
        Tendência dos últimos 6 meses
      </button>

      {aberto && (
        erro ? (
          <div className="cat-trend-empty">Não foi possível carregar a tendência.</div>
        ) : !series ? (
          <div className="cat-trend-empty">Carregando…</div>
        ) : series.length === 0 ? (
          <div className="cat-trend-empty">Sem gastos categorizados no período.</div>
        ) : (
          <div className="cat-trend-list">
            {series.slice(0, 8).map(s => {
              const max = Math.max(...s.points.map(p => p.total), 1);
              return (
                <div className="cat-trend-row" key={s.categoryId ?? '__none__'}>
                  <span className="cat-trend-name" title={s.categoryName}>{s.categoryName}</span>
                  <span className="cat-trend-avg">{fmt(s.average)}<em>/mês</em></span>
                  <div className="cat-trend-spark" title={s.points.map(p => `${p.month}: ${fmt(p.total)}`).join('\n')}>
                    {s.points.map((p, i) => (
                      <span
                        key={p.month}
                        /* A ÚLTIMA barra é o mês exibido, e vem cheia. Com as
                           seis no mesmo tom não dá para dizer onde é "agora" —
                           e "agora" é o que a pessoa está comparando com o
                           resto. */
                        className={`cat-trend-bar${i === s.points.length - 1 ? ' atual' : ''}`}
                        style={{ height: `${Math.max((p.total / max) * 100, 4)}%`, background: s.color }}
                      />
                    ))}
                  </div>
                  {s.trendPct === null ? (
                    <span className="cat-trend-badge neutral" title="Sem base de comparação no início do período">—</span>
                  ) : (
                    <span className={`cat-trend-badge ${s.trendPct > 0 ? 'up' : 'down'}`}>
                      {s.trendPct > 0 ? '↑' : '↓'} {formatPct(Math.abs(s.trendPct), 0)}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        )
      )}
    </div>
  );
};

export default CategoryChart;
